import { Grid } from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../Session';
import { useGetUserListByTypeQuery, UserType, SettingValueUnit, SaleTransactionStage } from '../../../api';
import OutlinedInputField from '../../../components/fields/OutlinedInputField';
import OutlinedNumericUnitInputField from '../../../components/fields/OutlinedNumericUnitInputField';
import OutlinedSelectField from '../../../components/fields/OutlinedSelectField';
import useCommonStyles from '../../../useCommonStyles';
import useBiddingSetting from '../../../utilities/useBiddingSetting';
import useSettingValueUnitDefaults from '../../../utilities/useSettingValueUnitDefaults';

const TransactionForm = ({ stage }: { stage?: SaleTransactionStage }) => {
    const { t } = useTranslation(['saleTransactionsPage', 'common']);
    const commonStyles = useCommonStyles();
    const user = useCurrentUser();

    const { setFieldValue, values } = useFormikContext<any>();
    const [{ value: handlingChargesUnit }] = useField<SettingValueUnit | ''>({ name: 'handlingCharges.unit' });
    const [{ value: marginUnit }] = useField<SettingValueUnit | ''>({ name: 'margin.unit' });

    const disableHandlingChargesAndMargin = useMemo(() => {
        if (user.type !== UserType.Admin && user.type !== UserType.SalesManager) {
            return true;
        }

        if (!stage) {
            return false;
        }

        return ![
            SaleTransactionStage.New,
            SaleTransactionStage.UpcomingBidding,
            SaleTransactionStage.BiddingOpen,
            SaleTransactionStage.ForAwarding,
        ].includes(stage);
    }, [stage, user.type]);

    const handlingChargesValue = values?.handlingCharges?.value;
    const marginValue = values?.margin?.value;

    const { biddingSetting } = useBiddingSetting('cache-first');
    const { handlingFeesSetting, marginSetting } = useMemo(() => {
        if (biddingSetting) {
            return {
                handlingFeesSetting: biddingSetting.handlingFees,
                marginSetting: biddingSetting.margin,
            };
        }

        return { handlingFeesSetting: undefined, marginSetting: undefined };
    }, [biddingSetting]);

    const { ensureUnitAndMaybeValue, onUnitChanged } = useSettingValueUnitDefaults(setFieldValue);

    useEffect(() => {
        ensureUnitAndMaybeValue('handlingCharges', handlingFeesSetting, handlingChargesUnit, handlingChargesValue);
        ensureUnitAndMaybeValue('margin', marginSetting, marginUnit, marginValue);
    }, [
        ensureUnitAndMaybeValue,
        handlingChargesUnit,
        handlingChargesValue,
        handlingFeesSetting,
        marginSetting,
        marginUnit,
        marginValue,
    ]);

    const { data } = useGetUserListByTypeQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            type: UserType.SaleConsultant,
        },
    });

    const salesConsultantOptions = useMemo(
        () =>
            (data?.users?.items || []).map(user => ({
                value: user.id,
                label: user.fullName,
            })),
        [data]
    );

    return (
        <Grid spacing={2} container>
            <Grid xs={12} item>
                <OutlinedInputField
                    color="secondary"
                    label={t('saleTransactionsPage:transactionDetails.internalRemarks')}
                    name="internalRemarks"
                    placeholder={t('saleTransactionsPage:transactionDetails.internalRemarks')}
                />
            </Grid>
            <Grid xs={12} item>
                <OutlinedNumericUnitInputField
                    color="secondary"
                    disabled={disableHandlingChargesAndMargin}
                    formatToFixed={2}
                    inputProps={{
                        classes: {
                            icon: commonStyles.fillSecondary,
                        },
                        inputMode: 'decimal',
                    }}
                    label={t('saleTransactionsPage:tradeInDetails.handlingCharges')}
                    minMax={{ percentage: { min: 0, max: 100 }, amount: { min: 0 } }}
                    name="handlingCharges"
                    onUnitChanged={unit =>
                        onUnitChanged('handlingCharges', handlingFeesSetting, unit as SettingValueUnit)
                    }
                    type="number"
                    required
                />
            </Grid>
            <Grid xs={12} item>
                <OutlinedNumericUnitInputField
                    color="secondary"
                    disabled={disableHandlingChargesAndMargin}
                    formatToFixed={2}
                    inputProps={{
                        classes: {
                            icon: commonStyles.fillSecondary,
                        },
                        inputMode: 'decimal',
                    }}
                    label={t('saleTransactionsPage:transactionDetails.margin')}
                    minMax={{ percentage: { min: 0, max: 100 }, amount: { min: 0 } }}
                    name="margin"
                    onUnitChanged={unit => onUnitChanged('margin', marginSetting, unit as SettingValueUnit)}
                    type="number"
                    required
                />
            </Grid>
            <Grid xs={12} item>
                <OutlinedSelectField
                    color="secondary"
                    inputProps={{
                        classes: {
                            icon: commonStyles.fillSecondary,
                        },
                    }}
                    label={t('saleTransactionsPage:tradeInDetails.salesConsultant')}
                    name="salesConsultant"
                    options={salesConsultantOptions}
                    placeholder={t('saleTransactionsPage:tradeInDetails.salesConsultant')}
                    required
                />
            </Grid>
        </Grid>
    );
};

export default TransactionForm;
